import { useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

interface MerchantFunnelData {
  merchantId: string;
  totalLeads: number;
  totalLeadsPercentage: number;
  totalDelivered: number;
  deliveredPercentage: number;
  totalRead: number;
  readPercentage: number;
  totalResponded: number;
  respondedPercentage: number;
  totalAppointmentIntention: number;
  appointmentIntentionPercentage: number;
  totalAppointments: number;
  appointmentsPercentage: number;
  totalTransfers: number;
  transfersPercentage: number;
  totalDiscarded: number;
  discardedPercentage: number;
  totalInactive: number;
  inactivePercentage: number;
}

interface FunnelTableProps {
  data: MerchantFunnelData | MerchantFunnelData[];
}

type SortColumn =
  | "merchantId"
  | "totalLeads"
  | "totalDelivered"
  | "deliveredPercentage"
  | "totalRead"
  | "readPercentage"
  | "totalResponded"
  | "respondedPercentage"
  | "totalAppointmentIntention"
  | "appointmentIntentionPercentage"
  | "totalAppointments"
  | "appointmentsPercentage"
  | "totalTransfers"
  | "transfersPercentage"
  | "totalDiscarded"
  | "discardedPercentage"
  | "totalInactive"
  | "inactivePercentage"
  | null;

type SortDirection = "asc" | "desc";

const FunnelTable = ({ data }: FunnelTableProps) => {
  const merchantsData = Array.isArray(data) ? data : [data];

  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortedData = () => {
    if (!sortColumn) return merchantsData;

    return [...merchantsData].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortColumn) {
        case "merchantId":
          aValue = a.merchantId;
          bValue = b.merchantId;
          break;
        case "totalLeads":
          aValue = a.totalLeads;
          bValue = b.totalLeads;
          break;
        case "totalDelivered":
          aValue = a.totalDelivered;
          bValue = b.totalDelivered;
          break;
        case "deliveredPercentage":
          aValue = a.deliveredPercentage;
          bValue = b.deliveredPercentage;
          break;
        case "totalRead":
          aValue = a.totalRead;
          bValue = b.totalRead;
          break;
        case "readPercentage":
          aValue = a.readPercentage;
          bValue = b.readPercentage;
          break;
        case "totalResponded":
          aValue = a.totalResponded;
          bValue = b.totalResponded;
          break;
        case "respondedPercentage":
          aValue = a.respondedPercentage;
          bValue = b.respondedPercentage;
          break;
        case "totalAppointmentIntention":
          aValue = a.totalAppointmentIntention;
          bValue = b.totalAppointmentIntention;
          break;
        case "appointmentIntentionPercentage":
          aValue = a.appointmentIntentionPercentage;
          bValue = b.appointmentIntentionPercentage;
          break;
        case "totalAppointments":
          aValue = a.totalAppointments;
          bValue = b.totalAppointments;
          break;
        case "appointmentsPercentage":
          aValue = a.appointmentsPercentage;
          bValue = b.appointmentsPercentage;
          break;
        case "totalTransfers":
          aValue = a.totalTransfers;
          bValue = b.totalTransfers;
          break;
        case "transfersPercentage":
          aValue = a.transfersPercentage;
          bValue = b.transfersPercentage;
          break;
        case "totalDiscarded":
          aValue = a.totalDiscarded;
          bValue = b.totalDiscarded;
          break;
        case "discardedPercentage":
          aValue = a.discardedPercentage;
          bValue = b.discardedPercentage;
          break;
        case "totalInactive":
          aValue = a.totalInactive;
          bValue = b.totalInactive;
          break;
        case "inactivePercentage":
          aValue = a.inactivePercentage;
          bValue = b.inactivePercentage;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === "asc" ? comparison : -comparison;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 inline text-gray-400" />;
    }
    return sortDirection === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-blue-500" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-blue-500" />;
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const columns: { key: string; label: string; minWidth: string; numeric?: boolean }[] = [
    { key: "merchantId", label: "Merchant ID", minWidth: "180px" },
    { key: "totalLeads", label: "Total Leads", minWidth: "100px", numeric: true },
    { key: "totalDelivered", label: "Delivered", minWidth: "90px", numeric: true },
    { key: "totalRead", label: "Read", minWidth: "80px", numeric: true },
    { key: "totalResponded", label: "Responded", minWidth: "90px", numeric: true },
    { key: "totalAppointmentIntention", label: "Apt. Intention", minWidth: "100px", numeric: true },
    { key: "totalAppointments", label: "Appointments", minWidth: "100px", numeric: true },
    { key: "totalTransfers", label: "Transfers", minWidth: "90px", numeric: true },
    { key: "totalDiscarded", label: "Discarded", minWidth: "90px", numeric: true },
    { key: "totalInactive", label: "Inactive", minWidth: "80px", numeric: true },
    { key: "deliveredPercentage", label: "Delivered %", minWidth: "90px", numeric: true },
    { key: "readPercentage", label: "Read %", minWidth: "80px", numeric: true },
    { key: "respondedPercentage", label: "Responded %", minWidth: "90px", numeric: true },
    { key: "appointmentIntentionPercentage", label: "Apt. Intention %", minWidth: "100px", numeric: true },
    { key: "appointmentsPercentage", label: "Appointments %", minWidth: "100px", numeric: true },
    { key: "transfersPercentage", label: "Transfers %", minWidth: "90px", numeric: true },
    { key: "discardedPercentage", label: "Discarded %", minWidth: "90px", numeric: true },
    { key: "inactivePercentage", label: "Inactive %", minWidth: "80px", numeric: true },
  ];

  return (
    <div className="w-full max-w-full">
      <p className="text-lg font-bold mb-4">Lead Funnel Analytics by Merchant</p>
      <div className="w-full max-w-full overflow-x-auto border border-gray-200 rounded-md bg-white">
        <table className="w-full min-w-[1200px] text-sm">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left font-medium text-gray-600 border-b border-r border-gray-200 cursor-pointer hover:bg-gray-100 ${col.numeric ? 'text-right' : ''}`}
                  style={{ minWidth: col.minWidth, position: 'sticky', left: col.key === 'merchantId' ? 0 : undefined, zIndex: col.key === 'merchantId' ? 10 : 1 }}
                  onClick={() => handleSort(col.key as SortColumn)}
                >
                  {col.label}
                  {renderSortIcon(col.key as SortColumn)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getSortedData().map((merchant, index) => (
              <tr key={merchant.merchantId || `merchant-${index}`} className="hover:bg-gray-50">
                <td
                  className="px-4 py-3 font-semibold text-blue-600 border-b border-r border-gray-200 whitespace-nowrap"
                  style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}
                >
                  {merchant.merchantId}
                </td>
                <td className="px-4 py-3 text-right font-bold text-lg">{formatNumber(merchant.totalLeads)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalDelivered)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalRead)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalResponded)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalAppointmentIntention)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalAppointments)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalTransfers)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalDiscarded)}</td>
                <td className="px-4 py-3 text-right">{formatNumber(merchant.totalInactive)}</td>
                <td className="px-4 py-3 text-right">{merchant.deliveredPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.readPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.respondedPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.appointmentIntentionPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.appointmentsPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.transfersPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.discardedPercentage.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right">{merchant.inactivePercentage.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {merchantsData.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No merchant data available for the selected filters</p>
        </div>
      )}
    </div>
  );
};

export default FunnelTable;
